import { PropsWithChildren, useEffect } from "react";
import { Pagination, Tooltip, theme } from "antd";
import {
  EntityWorkContext,
  entityWorkContext,
  useEntityWork,
  useEntityWorkContext,
} from "./useEntityWork";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { EntitySummary, ExpandedView } from "./components/EntitySummary";
import { CollapsibleComment } from "./components/CollapsibleComment";
import { CollapsibleRow } from "./components/CollapsibleRow";
import { TagsList } from "./components/TagsList";
import { Flex } from "src/components/Flex";
import { Time } from "src/components/Time";
import { VzblTag } from "./components/VzblTag";
import { Section } from "./components/Section";
import { EntityLink } from "./components/EntityLink";
import { useApplicationContext } from "src/hooks/useApplication";
import { useEntityContext } from "./useEntityContext";
import { LoadingOutlined } from "@ant-design/icons";

export const Work = ({
  children,
  orgId,
  entityUuid,
  actorUuid,
  types,
  search,
  pagingSize,
}: PropsWithChildren<
  Omit<EntityWorkContext, "query" | "setPagination" | "dataRef">
>) => {
  const entityWork = useEntityWork({
    orgId,
    entityUuid,
    actorUuid,
    types,
    search,
    pagingSize,
  });
  return (
    <entityWorkContext.Provider value={entityWork}>
      {children}
    </entityWorkContext.Provider>
  );
};

export const WorkComponent = (props: {
  aggregate?: boolean;
  hasResultsCallback?: (val: boolean) => void;
  title?: string;
}) => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const { query, setPagination } = useEntityWorkContext();
  const { token } = theme.useToken();
  // const orgId = useOrg().id;
  // this is for expanding each row to work.
  query.data?.data.forEach((item: any) => {
    item.key = item.entity.properties.uuid;
  });
  useEffect(() => {
    if (query.data?.data && props.aggregate && props.hasResultsCallback) {
      props.hasResultsCallback(query.data?.data.length > 0);
    }
  }, [query, props]);
  return (
    <Section
      title={props.title}
      isEmpty={query.data?.data.length === 0}
      style={query.data?.data.length === 0 ? {} : { paddingTop: 0 }}
    >
      {query.isLoading && (
        <Flex centered style={{ padding: 16 }}>
          <LoadingOutlined />
        </Flex>
      )}
      {query.data?.data.length === 0 && (
        <div style={{ color: token.colorTextTertiary }}>None</div>
      )}
      {query.data?.data.map((item: any, index: number) => {
        const issueKey = item.entity.properties.issueKey;
        const title =
          item.entity.properties.title ||
          item.entity.properties.message ||
          item.entity.properties.name;
        const tooltip = item.entity.properties.body || title;
        const name = issueKey ? `${issueKey} - ${title}` : title;
        const typeStatusCombo = [
          item.entity.labels[0],
          item.entity.properties.status || item.entity.properties.state,
        ].filter((item) => !!item);
        // const href = `/${orgId}/entity/${item.entity.properties.uuid}`;
        return (
          <CollapsibleRow
            key={index}
            arrowStyle={{
              marginTop: 6,
            }}
            arrowSize={40}
            style={{
              borderBottom: `1px solid ${token.colorBorderSecondary}99`,
            }}
            expand={true}
            preview={
              <Flex spaceBetween style={{ alignItems: "center" }}>
                <div
                  className="trunc entity"
                  style={{
                    marginTop: 6,
                    marginBottom: 6,
                  }}
                >
                  <Tooltip title={tooltip}>
                    <EntityLink
                      uuid={item.entity.properties.uuid}
                      style={{ display: "flex" }}
                    >
                      <IntegrationIcon
                        source={item.entity.properties.integration}
                        size={22}
                        style={{
                          marginRight: 10,
                          alignItems: "center",
                          height: 30,
                        }}
                      />
                      <div className="trunc">{name}</div>
                    </EntityLink>
                  </Tooltip>
                </div>
                <Flex style={{ whiteSpace: "nowrap" }}>
                  <TagsList
                    tags={[...new Set(typeStatusCombo)]}
                    tag={(tag: string, index: number) => (
                      <VzblTag
                        orgId={orgId}
                        context={context}
                        text={tag}
                        key={index}
                      />
                    )}
                    threshold={2}
                  />
                  <Time
                    value={item.lastActivity}
                    style={{ marginLeft: 10 }}
                    className="entity"
                  />
                </Flex>
              </Flex>
            }
            content={
              <ExpandedView>
                <EntitySummary entity={item.entity} key={index} />
              </ExpandedView>
            }
          />
        );
      })}
      {query.data && (
        <Pagination
          size="small"
          hideOnSinglePage={true}
          current={query.data?.meta.page}
          total={query.data?.meta.total}
          pageSize={query.data?.meta.pageSize}
          showSizeChanger={false}
          showTotal={(t, [r1, r2]) =>
            (r1 > 1 || r2 < t) && (
              <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                showing {r1}-{r2} of {t}
              </div>
            )
          }
          onChange={(page, pageSize) => {
            setPagination({ page, pageSize });
          }}
          style={{ textAlign: "right", marginTop: 10 }}
        />
      )}
    </Section>
  );
};

export const WorkCommentsComponent = (props: {
  title: string;
  targetEntity?: any;
}) => {
  const { query, setPagination } = useEntityWorkContext();
  const { token } = theme.useToken();
  return (
    <Section title={props.title} isEmpty={query.data?.data.length === 0}>
      {query.isLoading && (
        <Flex centered style={{ padding: 32 }}>
          <LoadingOutlined />
        </Flex>
      )}
      {query.data?.data.length === 0 && (
        <div style={{ color: token.colorTextTertiary }}>None</div>
      )}
      {query.data?.data.map((item: any) => {
        return (
          <CollapsibleComment
            key={item.entity.properties.uuid}
            item={item}
            targetEntity={props.targetEntity}
          />
        );
      })}
      {query.data && (
        <Pagination
          size="small"
          hideOnSinglePage={true}
          current={query.data?.meta.page}
          total={query.data?.meta.total}
          pageSize={query.data?.meta.pageSize}
          showSizeChanger={false}
          showTotal={(t, [r1, r2]) =>
            (r1 > 1 || r2 < t) && (
              <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                showing {r1}-{r2} of {t}
              </div>
            )
          }
          onChange={(page, pageSize) => {
            setPagination({ page, pageSize });
          }}
          style={{ textAlign: "right", marginTop: 10 }}
        />
      )}
    </Section>
  );
};
